import PremiumBannerTooltip from "@/components/PremiumFeatureComponents/PremiumBannerTooltip";
import { useCompanyAccess } from "@/hooks/useCompanySettings";
import useImpactMethod from "@/hooks/useImpactMethod";
import { setLocalStorageMetricKey } from "@/pages/dashboard/hooks/useImpactMetric";
import {
  IMPACT_METRICS,
  ImpactMetricGroupIcons,
  ImpactMetricGroupKey,
  ImpactMetricKey,
  PREMIUM_IMPACT_METRIC_KEYS,
} from "@/pages/dashboard/utils/types";
import { InfoCircleOutlined } from "@ant-design/icons";
import { Space, Tooltip, TreeSelect } from "antd";
import { TreeSelectProps } from "antd/es/tree-select";
import { BaseSelectRef } from "rc-select";
import { useEffect, useRef, useState } from "react";
import styles from "./MetricSelector.module.scss";

const impactMetricGroupKeys: ImpactMetricGroupKey[] = [
  "Main impacts",
  "Carbon footprint",
  "Biodiversity",
  "Human health",
  "Resource scarcity",
];

function MetricSelect(props: TreeSelectProps) {
  const access = useCompanyAccess();

  const treeSelectRef = useRef<BaseSelectRef>(null);
  const [listHeight, setListHeight] = useState(30);
  const { indicators } = useImpactMethod();
  const selectedMetric = IMPACT_METRICS[props.value as ImpactMetricKey];

  const hasAccess = (key: string) => {
    return !access || access.includes("metric:" + key) || !PREMIUM_IMPACT_METRIC_KEYS.includes(key);
  };

  if (selectedMetric) {
    if (!hasAccess(selectedMetric.key)) {
      setLocalStorageMetricKey(null);
    }
  }

  const treeOptionData = impactMetricGroupKeys
    .map((groupKey) => {
      const subOptions = Object.values(indicators)
        .filter((metric) => metric.impactGroup === groupKey && groupKey !== "Main impacts")
        .map((metric) => {
          return {
            key: metric.key,
            icon: metric.icon,
            label: (
              <PremiumBannerTooltip
                placement={"right"}
                show={!hasAccess(metric.key)}
              >
                <div style={{ width: "100%" }}>{metric.label}</div>
              </PremiumBannerTooltip>
            ),
            value: metric.key,
            disabled: !hasAccess(metric.key),
          };
        });

      return {
        title: <span>{groupKey}</span>,
        icon: ImpactMetricGroupIcons[groupKey],
        value: groupKey,
        selectable: false,
        children: subOptions,
      };
    })
    .filter((option) => option.children.length > 0);

  const mainOptions = Object.values(indicators)
    .filter((ind) => ind.impactGroup === "Main impacts")
    .map((metric) => {
      return {
        title: (
          <div style={{ display: "inline-block", width: "90%" }}>
            <PremiumBannerTooltip
              placement={"right"}
              show={!hasAccess(metric.key)}
            >
              <div style={{ width: "100%" }}>{metric.label}</div>
            </PremiumBannerTooltip>
          </div>
        ),
        icon: metric.icon,
        value: metric.key,
        disabled: !hasAccess(metric.key),
        children: [],
      };
    })
    .sort((a, b) => {
      if (a.value === "carbon_footprint") return -1;
      if (b.value === "carbon_footprint") return 1;
      if (a.value === "environmental_cost") return -1;
      if (b.value === "environmental_cost") return 1;
      if (a.value === "ef_environmental_cost") return -1;
      if (b.value === "ef_environmental_cost") return 1;
      if (a.value === "land_use") return -1;
      if (b.value === "land_use") return 1;
      if (a.value === "ef_land_use") return -1;
      if (b.value === "ef_land_use") return 1;
      if (a.value === "water_use") return -1;
      if (b.value === "water_use") return 1;
      if (a.value === "ef_water_use") return -1;
      if (b.value === "ef_water_use") return 1;
      return 1;
    });

  useEffect(() => {
    const updateGap = () => {
      if (treeSelectRef.current) {
        const rect = treeSelectRef.current?.nativeElement.getBoundingClientRect();
        const spaceBelow = (window.visualViewport?.height || 0) - rect.bottom - 15;
        setListHeight(spaceBelow);
      }
    };

    updateGap(); // initial calculation
    window.addEventListener("resize", updateGap);
    window.addEventListener("scroll", updateGap);

    return () => {
      window.removeEventListener("resize", updateGap);
      window.removeEventListener("scroll", updateGap);
    };
  }, []);

  return (
    <Space
      className={styles.container}
      size={12}
    >
      <TreeSelect
        ref={treeSelectRef}
        treeData={[...mainOptions, ...treeOptionData]}
        className={styles.metricSelector}
        treeIcon={true}
        prefix={<span className={styles.selectPrefix}>Impact</span>}
        {...props}
        listHeight={listHeight}
        treeExpandAction={"click"}
      />
      <Tooltip
        title={selectedMetric.unitTooltip}
        placement={"right"}
      >
        <Space className={styles.unitTooltip}>
          {selectedMetric.unit}
          <InfoCircleOutlined />
        </Space>
      </Tooltip>
    </Space>
  );
}

export default MetricSelect;
